import React, { useState, useEffect } from "react";
import { Table, Button, Space, message, Popconfirm, Card, Input, Modal } from "antd";
import { useSearchParams } from "react-router-dom";
import api from "../../api";
import { EyeOutlined } from "@ant-design/icons";
import { debounce } from "lodash";

const OpenOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Get initial values from URL
  const initialSearch = searchParams.get('search') || "";
  const initialPage = parseInt(searchParams.get('page')) || 1;

  const [searchTerm, setSearchTerm] = useState(initialSearch);

  const updateURLParams = (newParams) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params);
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('page', pagination.current);
    params.append('per_page', pagination.pageSize);

    if (searchTerm) {
      params.append('search', searchTerm);
    }

    return params.toString();
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const queryParams = buildQueryParams();
      const response = await api.get(`/panel/order/open?${queryParams}`);
      setOrders(response.data.data);
      setPagination({
        current: response.data.meta.current_page,
        pageSize: response.data.meta.per_page,
        total: response.data.meta.total_users,
      });
    } catch (error) {
      message.error("خطا در دریافت سفارشات باز");
      console.error("خطا در دریافت سفارشات باز:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: initialPage }));
    fetchOrders();
  }, [searchParams]);

  const handleSearch = debounce((value) => {
    setSearchTerm(value);
    updateURLParams({ search: value, page: 1 });
  }, 300);

  const handlePaginationChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
    updateURLParams({ page });
  };

  const showOrderDetails = (order) => {
    setSelectedOrder(order);
    setModalVisible(true);
  };

  const itemsColumns = [
    {
      title: "محصول",
      dataIndex: ["post_id", "title"],
      key: "product",
    },
    {
      title: "کد فنی",
      dataIndex: ["post_id", "technical_code"],
      key: "technical_code",
    },
    {
      title: "برند",
      dataIndex: ["brand_id", "name"],
      key: "brand",
    },
    {
      title: "فروشنده",
      dataIndex: ["seller_id", "name"],
      key: "seller",
    },
    {
      title: "تعداد",
      dataIndex: "total_count",
      key: "count",
    },
    {
      title: "قیمت",
      dataIndex: "total_price",
      key: "price",
      render: (price) => `${parseInt(price).toLocaleString()} تومان`,
    },
  ];

  const columns = [
    {
      title: "کاربر",
      dataIndex: "user",
      key: "user",
      render: (user) => (
        <>
          <p><strong>نام:</strong> {user.name}</p>
          <p><strong>شماره:</strong> {user.number}</p>
          <p><strong>ایمیل:</strong> {user.email}</p>
        </>
      ),
    },
    {
      title: " قیمت کل",
      dataIndex: "total_overall_price",
      key: "total_overall_price",
      render: (total_overall_price) => `${total_overall_price.toLocaleString()} تومان`,

    },
    {
        title: "تاریخ ایجاد",
        dataIndex: "created_at",
        key: "created_at",
      },
    {
      title: "عملیات",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EyeOutlined />} 
            onClick={() => showOrderDetails(record)}
          >
            جزئیات
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card title="سفارشات باز">
      <div className="mb-4">
        <Input
          placeholder="جستجو..."
          onChange={(e) => handleSearch(e.target.value)}
          value={searchTerm}
          allowClear
          style={{ width: 300, marginBottom: 16 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={orders}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: handlePaginationChange,
        }}
        rowKey={(record) => record.user.id}
      />

      <Modal
        title={`جزئیات سفارش کاربر ${selectedOrder?.user.name}`}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={1000}
      >
        {selectedOrder && (
          <>
            <div style={{ marginBottom: 16 }}>
              <p><strong>نام کاربر:</strong> {selectedOrder.user.name}</p>
              <p><strong>شماره تماس:</strong> {selectedOrder.user.number}</p>
              <p><strong>ایمیل:</strong> {selectedOrder.user.email}</p>
            </div>

            <Table
              columns={itemsColumns}
              dataSource={selectedOrder.items}
              rowKey={(item) => item.post_id.id}
              pagination={false}
            />
          </>
        )}
      </Modal>
    </Card>
  );
};

export default OpenOrders;